// glonet.ai /hosts/dashboard — the host's own numbers.
// AI Ops Platform build-order item 4: "Hosts cannot see their own money today.
// We already work it out — members, MRR, what they are owed — but only admins
// can see it. A host-only version of that screen is a query, not a rebuild."
//
// Deliberately the same shape as AffiliateDashboard.js (shipped 2026-08-12),
// minus everything a host does not need:
//   Today   - your nexus, the one next step, four numbers, your split in words
//   Members - joined, profiled, the gap, and what to do about it
//   Money   - MRR, paid, owed, recent payouts
//
// The honesty rules it carries:
//   · Nobody pays during early access. A $0 must read as "not started yet",
//     never as a disappointment and never as a promise.
//   · A fee change applies to FUTURE payments only. Splits are computed per
//     invoice, so it is already true in code — it just has to be said.
//   · Counts, not a list of names to stare at. "Nudge 3" beats three rows.
//
// ?preview=1 renders clearly-labeled SAMPLE numbers with no backend.
// ?as_user_id=N is the ADMIN view-as (2026-08-24): this host's real screen,
// read-only, reached from a link on their row in Admin → Hosts. The admin table
// says how a host is doing; this says what the host is looking at.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";

const ACCENT = "#74a0f5";
const GREEN = "#57d9a4";
const AMBER = "#e0a35e";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

const TABS = [["today", "Today"], ["members", "Members"], ["money", "Money"]];

export const PREVIEW_HOST = {
  is_host: true, name: "Sample Host",
  nexuses: [
    { event_id: 1, event_name: "Climate Operators", members: 84, profiled: 61, joined_7d: 6,
      affiliates: 2, matching_enabled: true, watercooler_enabled: true,
      active_subs: 31, mrr_cents: 24800 },
    { event_id: 2, event_name: "Weekend Builders", members: 22, profiled: 9, joined_7d: 1,
      affiliates: 0, matching_enabled: false, watercooler_enabled: false,
      active_subs: 0, mrr_cents: 0 },
  ],
  members: 106, profiled: 70, joined_7d: 7, mrr_cents: 24800, active_subs: 31,
  paid_cents: 412300, owed_cents: 17360, paid_30d: 68200,
  recent_payouts: [
    { amount_cents: 8600, status: "pending", stream: "subscription", at: "2026-08-10T00:00:00Z" },
    { amount_cents: 8760, status: "sent", stream: "subscription", at: "2026-08-03T00:00:00Z" },
    { amount_cents: 9100, status: "sent", stream: "subscription", at: "2026-07-27T00:00:00Z" },
    { amount_cents: 4500, status: "sent", stream: "one_time", at: "2026-07-21T00:00:00Z" },
  ],
  payout_ready: true, platform_fee_pct: 30, host_share_pct: 70, matching_on: true,
  earning_started: true,
  next_step: { title: "Nudge 36 members with no profile",
               why: "Only members who finish a profile can be matched. They are the ones who move the room.",
               cta: "See members", act: "members" },
};

const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 18, padding: "26px 28px" };
const label = { fontFamily: MONO, fontSize: 11, letterSpacing: "0.2em",
  textTransform: "uppercase", color: "#6b6f78" };
const primaryBtn = { fontSize: 14, fontWeight: 600, color: "#0c0d10", background: ACCENT,
  border: "none", cursor: "pointer", padding: "11px 20px", borderRadius: 999 };

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function HostDashboard() {
  const navigate = useNavigate();
  const qs = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const preview = qs.get("preview") === "1";
  const asUserId = qs.get("as_user_id") || "";
  const asParam = asUserId ? `?as_user_id=${encodeURIComponent(asUserId)}` : "";
  const initialTab = TABS.some(([k]) => k === qs.get("tab")) ? qs.get("tab") : "today";
  const [tab, setTabState] = useState(initialTab);
  const setTab = (k) => {
    setTabState(k);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", k);
      window.history.replaceState(null, "", u.toString());
    } catch (e) { /* history is cosmetic */ }
  };
  const [state, setState] = useState(preview ? "ready" : "loading"); // loading|anon|none|ready
  const [d, setD] = useState(preview ? PREVIEW_HOST : null);

  useEffect(() => {
    if (preview) return;
    Auth.currentAuthenticatedUser()
      .then(() => import("../services/api"))
      .then(({ default: api }) => api.get(`subscriptions/hosts/my-nexus${asParam}`, { timeout: 20000 }))
      .then(({ data }) => { setD(data); setState(data.is_host ? "ready" : "none"); })
      .catch((e) => setState(e?.response?.status === 403 ? "forbidden" : "anon"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gap = d ? Math.max(0, d.members - d.profiled) : 0;
  // In admin view-as these buttons would walk the ADMIN into their own payout
  // setup or nexus list while the page still reads as the host's — looking must
  // not turn into doing by accident. Tab switches stay live.
  const blocked = (a) => !!asUserId && a !== "members";
  const act = (a) => {
    if (blocked(a)) return;
    if (a === "payouts") navigate("/events/connect-setup");
    else if (a === "invite") navigate("/nexuses");
    else setTab("members");
  };

  const FourNumbers = () => (
    <section className="hd-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)",
      gap: 18, marginTop: 18 }}>
      {[
        ["Members", d.members, `${d.joined_7d} joined this week`],
        ["With a profile", d.profiled, "only these can be matched"],
        ["Owed to you", dollars(d.owed_cents), "released when payments settle"],
        ["Paid to you", dollars(d.paid_cents), "all-time, via Stripe"],
      ].map(([t, v, sub]) => (
        <div key={t} style={card}>
          <div style={label}>{t}</div>
          <div style={{ fontFamily: MONO, fontSize: 34, marginTop: 6 }}>{v}</div>
          <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 6 }}>{sub}</div>
        </div>
      ))}
    </section>
  );

  return (
    <div data-testid="host-dash" style={{ background: "#0c0d10", color: "#f1f2f4", minHeight: "100vh",
      fontFamily: "'Hanken Grotesk',system-ui,sans-serif", WebkitFontSmoothing: "antialiased" }}>
      <style>{`.hd-btn:hover { filter: brightness(1.08); }
        @media (max-width:840px){ .hd-grid{ grid-template-columns:1fr !important; } }`}</style>

      <nav style={{ maxWidth: 1040, margin: "0 auto", padding: "26px 32px",
        display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <a href="/hosts" onClick={(e) => { e.preventDefault(); navigate("/hosts"); }}
           style={{ textDecoration: "none", color: "inherit" }}>
          <span style={{ fontFamily: SERIF, fontSize: 25 }}>glonet<span style={{ color: ACCENT }}>.ai</span></span>
          <span style={{ ...label, marginLeft: 12 }}>Host&nbsp;Dashboard</span>
        </a>
        <a href="/nexuses" onClick={(e) => { e.preventDefault(); navigate("/nexuses"); }}
           style={{ color: "#b6b9c0", textDecoration: "none", fontSize: 14 }}>Your nexuses →</a>
      </nav>

      <main style={{ maxWidth: 1040, margin: "0 auto", padding: "6px 32px 90px" }}>
        {preview && (
          <div style={{ ...label, color: AMBER, border: `1px dashed ${AMBER}`, borderRadius: 10,
            padding: "10px 14px", marginBottom: 18 }}>
            Preview — sample numbers, not real data. Remove ?preview=1 for your own nexus.
          </div>
        )}
        {/* Whose screen this is — named, so an admin can never mistake a host's
            numbers for their own. */}
        {d?.viewing_as && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
            fontSize: 13.5, color: ACCENT, border: `1px solid ${ACCENT}`,
            background: "rgba(116,160,245,0.09)", borderRadius: 10,
            padding: "10px 14px", marginBottom: 18 }}>
            <span style={{ ...label, color: ACCENT }}>Admin view</span>
            <span style={{ color: "#d7dae0" }}>
              You are looking at <strong>{d.viewing_as.name || d.viewing_as.email}</strong>
              {d.viewing_as.name ? ` (${d.viewing_as.email})` : ""} — their real numbers,
              exactly as they see them. Read-only: nothing here acts on their behalf.
            </span>
            {/* /admin restores the last tab from sessionStorage — see the note
                on the matching link in AffiliateDashboard. */}
            <a href="/admin" style={{ color: ACCENT, marginLeft: "auto" }}>← back to admin</a>
          </div>
        )}
        {state === "loading" && <div style={{ color: "#7c818b" }}>Loading your numbers…</div>}

        {state === "forbidden" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 12 }}>
              Viewing someone else&apos;s dashboard is for admins.
            </div>
            <div style={{ color: "#9a9ea7", fontSize: 15 }}>
              Drop the <span style={{ fontFamily: MONO }}>?as_user_id=</span> from the address
              to see your own.
            </div>
          </div>
        )}

        {state === "anon" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 12 }}>Sign in to see your nexus.</div>
            <button className="hd-btn" onClick={() => navigate("/signin", { state: { from: "/hosts/dashboard" } })}
                    style={{ ...primaryBtn, fontSize: 15, padding: "13px 26px" }}>Sign in</button>
          </div>
        )}

        {state === "none" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 10 }}>You don't host a nexus yet.</div>
            <div style={{ color: "#9a9ea7", marginBottom: 24, fontSize: 15 }}>
              A nexus is your own corner of Glonet — your people, your price, your Water Cooler.
            </div>
            <div style={{ marginBottom: 18 }}>
              <a href="/hosts/dashboard?preview=1" style={{ color: "#7c818b", fontSize: 13 }}>
                See a preview of the dashboard →
              </a>
            </div>
            <button className="hd-btn" onClick={() => navigate("/hosts")}
                    style={{ ...primaryBtn, fontSize: 15, padding: "13px 26px" }}>How hosting works</button>
          </div>
        )}

        {state === "ready" && d && (
          <>
            <div data-testid="host-dash-tabs" style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "6px 0 20px" }}>
              {TABS.map(([k, t]) => (
                <button key={k} className="hd-btn" data-testid={`host-dash-tab-${k}`} onClick={() => setTab(k)}
                        style={{ fontFamily: MONO, fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase",
                          color: tab === k ? "#0c0d10" : "#b6b9c0",
                          background: tab === k ? ACCENT : "rgba(255,255,255,0.05)",
                          border: "1px solid " + (tab === k ? ACCENT : "rgba(255,255,255,0.12)"),
                          cursor: "pointer", padding: "9px 18px", borderRadius: 999 }}>
                  {t}
                </button>
              ))}
            </div>

            {/* ————— TODAY ————— */}
            {tab === "today" && (
              <>
                <section className="hd-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr",
                  gap: 18 }}>
                  <div style={{ ...card, borderColor: "rgba(116,160,245,0.35)" }}>
                    <div style={label}>Do this next</div>
                    <div style={{ fontFamily: SERIF, fontSize: 24, margin: "10px 0 6px" }}>{d.next_step.title}</div>
                    <div style={{ color: "#9a9ea7", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                      {d.next_step.why}
                    </div>
                    <button className="hd-btn" onClick={() => act(d.next_step.act)}
                            disabled={blocked(d.next_step.act)}
                            style={{ ...primaryBtn,
                              ...(blocked(d.next_step.act) ? { opacity: 0.45, cursor: "default" } : {}) }}>
                      {d.next_step.cta}
                    </button>
                  </div>
                  <div style={card}>
                    <div style={label}>Your nexus{d.nexuses.length > 1 ? "es" : ""}</div>
                    <div style={{ marginTop: 12 }}>
                      {d.nexuses.map((n) => (
                        <div key={n.event_id} style={{ display: "flex", justifyContent: "space-between",
                          gap: 12, padding: "9px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                          <div>
                            <div style={{ fontSize: 15 }}>{n.event_name || `Nexus ${n.event_id}`}</div>
                            <div style={{ color: "#7c818b", fontSize: 12.5 }}>
                              {n.members} member{n.members === 1 ? "" : "s"} · {n.profiled} profiled
                              {n.matching_enabled ? " · matching on" : " · matching off"}
                            </div>
                          </div>
                          <div style={{ fontFamily: MONO, fontSize: 15, whiteSpace: "nowrap" }}>
                            {dollars(n.mrr_cents)}<span style={{ color: "#6b6f78", fontSize: 12 }}>/mo</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>

                <FourNumbers />

                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Your split</div>
                  <div style={{ color: "#b6b9c0", fontSize: 15, marginTop: 10, maxWidth: "70ch", lineHeight: 1.7 }}>
                    You keep <b style={{ color: "#f1f2f4" }}>{d.host_share_pct}%</b> of what your
                    members actually pay; Glonet takes {d.platform_fee_pct}% and covers the
                    matching, the Water Cooler, the agents and the card fees out of it.
                  </div>
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 12, maxWidth: "72ch" }}>
                    If this ever changes it applies to <b>future payments only</b> — every payment
                    is split when it happens, so nothing already paid can be rewritten.
                    {!d.earning_started && (
                      <> Nobody is paying yet: joining is free until matching turns on, so the
                      money columns start moving on launch day, not before.</>
                    )}
                  </div>
                </section>

                {!d.payout_ready && (
                  <section style={{ ...card, marginTop: 18, borderColor: `${AMBER}55` }}>
                    <div style={{ ...label, color: AMBER }}>Payouts not set up</div>
                    <div style={{ color: "#b6b9c0", fontSize: 14.5, marginTop: 10, maxWidth: "68ch" }}>
                      Your share is worked out either way and nothing is lost — but it is held
                      until there is a Stripe account for it to land in.
                    </div>
                    <button className="hd-btn" onClick={() => act("payouts")}
                            disabled={blocked("payouts")}
                            style={{ ...primaryBtn, marginTop: 14,
                              ...(blocked("payouts") ? { opacity: 0.45, cursor: "default" } : {}) }}>
                      Set up payouts
                    </button>
                  </section>
                )}
              </>
            )}

            {/* ————— MEMBERS ————— */}
            {tab === "members" && (
              <>
                <section style={card}>
                  <div style={label}>Who is in, and who is ready</div>
                  <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginTop: 16 }}>
                    {[["Joined", d.members], ["Finished a profile", d.profiled],
                      ["No profile yet", gap], ["Joined this week", d.joined_7d]].map(([t, v]) => (
                      <div key={t}>
                        <div style={{ fontFamily: MONO, fontSize: 32,
                          color: t === "No profile yet" && v > 0 ? AMBER : "#f1f2f4" }}>{v}</div>
                        <div style={{ color: "#7c818b", fontSize: 13 }}>{t}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", marginTop: 20 }}>
                    <div style={{ height: "100%", borderRadius: 999, background: GREEN,
                      width: `${d.members ? (d.profiled / d.members) * 100 : 0}%`, transition: "width .5s" }} />
                  </div>
                  <div style={{ color: "#9a9ea7", fontSize: 14, marginTop: 14, maxWidth: "70ch", lineHeight: 1.7 }}>
                    {gap === 0 && d.members > 0
                      ? "Everyone who joined has a profile. That is unusual and it is the whole game — matching only sees members who filled one in."
                      : gap > 0
                        ? `${gap} member${gap === 1 ? " has" : "s have"} joined but not filled in what they are looking for, so matching cannot see ${gap === 1 ? "them" : "them"} yet. A short personal note works better than anything automatic.`
                        : "Nobody has joined yet. Ten people is roughly where matching starts having something to work with."}
                  </div>
                </section>

                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Per nexus</div>
                  <table style={{ width: "100%", marginTop: 14, fontSize: 13.5, borderCollapse: "collapse" }}>
                    <tbody>
                      {d.nexuses.map((n) => (
                        <tr key={n.event_id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                          <td style={{ padding: "9px 0" }}>{n.event_name || `Nexus ${n.event_id}`}</td>
                          <td style={{ fontFamily: MONO, textAlign: "right", width: "14%" }}>{n.members}</td>
                          <td style={{ fontFamily: MONO, textAlign: "right", width: "14%", color: GREEN }}>{n.profiled}</td>
                          <td style={{ textAlign: "right", width: "26%", color: "#7c818b", fontSize: 12.5 }}>
                            {n.affiliates > 0 && `${n.affiliates} affiliate${n.affiliates === 1 ? "" : "s"} · `}
                            {n.matching_enabled ? "matching on" : "matching off"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </>
            )}

            {/* ————— MONEY ————— */}
            {tab === "money" && (
              <>
                <FourNumbers />
                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Money</div>
                  <div className="hd-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)",
                    gap: 12, marginTop: 12 }}>
                    {[["Monthly recurring", dollars(d.mrr_cents)],
                      ["Paying members", d.active_subs],
                      ["Last 30 days paid", dollars(d.paid_30d)],
                      ["All-time paid", dollars(d.paid_cents)]].map(([t, v]) => (
                      <div key={t} style={{ background: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: "12px 14px" }}>
                        <div style={{ ...label, fontSize: 10.5, letterSpacing: "0.14em" }}>{t}</div>
                        <div style={{ fontFamily: MONO, fontSize: 20, marginTop: 6 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {(d.recent_payouts || []).length > 0 ? (
                    <table style={{ width: "100%", marginTop: 18, fontSize: 13.5, borderCollapse: "collapse" }}>
                      <tbody>
                        {d.recent_payouts.map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                            <td style={{ padding: "8px 0", color: "#9a9ea7", width: "24%" }}>
                              {r.at ? new Date(r.at).toLocaleDateString() : ""}</td>
                            <td style={{ color: "#7c818b" }}>
                              {r.stream === "one_time" ? "room / ticket" : "subscription"}</td>
                            <td style={{ fontFamily: MONO }}>{dollars(r.amount_cents)}</td>
                            <td style={{ textAlign: "right", fontFamily: MONO, fontSize: 11,
                              letterSpacing: "0.1em", textTransform: "uppercase",
                              color: r.status === "sent" ? GREEN : r.status === "held" ? AMBER : "#9aa1ad" }}>
                              {r.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: "#9a9ea7", fontSize: 14, marginTop: 16, maxWidth: "70ch" }}>
                      {d.members === 0
                        ? "Nothing yet — money starts when members join and start paying."
                        : `${d.members} member${d.members === 1 ? "" : "s"} joined, ${d.active_subs} paying, so nothing has been paid out yet. Joining is free until matching turns on; earning starts then.`}
                    </div>
                  )}
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 14, maxWidth: "72ch" }}>
                    Payouts are Stripe transfers, sent when your members' payments settle. Only
                    money actually collected is ever paid out — a refunded or cancelled month
                    never pays.
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
